import { useState, useCallback, useRef } from 'react';
import purchaseOrderService from '../services/purchaseOrderService';
import ordersService from '../services/ordersService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';
import useProductSizes from './useProductSizes';
import { useWarehouseContext } from '../context/WarehouseContext';
import { formatBulkDeleteMessage } from '../utils/format';


/**
 * Hook: usePurchaseOrder
 * - Provides CRUD and list operations for purchase orders, advices, and challans
 * - References product sizes hook so UI can show product-size related data when creating an order
 */
const usePurchaseOrder = () => {
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const { selectedWarehouseIds } = useWarehouseContext();

    const { showCreate, showUpdate, showDelete, showError } = useMessageAlert();

    // Reference to product sizes hook so callers get access to product size options
    const productSizesHook = useProductSizes();
    const { productSizes, fetchProductSizes } = productSizesHook || {};

    // Deduplication & in-flight tracking
    const lastSignatureRef = useRef('');
    const lastFetchTimeRef = useRef(0);
    const inFlightRef = useRef(new Set());

    const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

    const shouldDedupe = useCallback((signature, force) => {
        if (force) return false;
        const now = Date.now();
        const timeDiff = now - lastFetchTimeRef.current;
        return signature === lastSignatureRef.current && timeDiff < 200;
    }, []);

    const fetchPurchaseOrders = useCallback(async (params = {}, options = {}) => {
        const { force = false } = options;
        const signature = generateSignature({ ...params, warehouse_ids: selectedWarehouseIds });

        if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
        if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

        setLoading(true);
        setError(null);
        inFlightRef.current.add(signature);

        try {
            const result = await purchaseOrderService.getAllPurchaseOrders(params, options) || {};
            if (result.success) {
                const data = result.data;
                // Sort by updated_at or created_at (newest first)
                const sortByDate = (arr) => {
                    return [...arr].sort((a, b) => {
                        const dateA = new Date(a.updated_at || a.created_at || 0);
                        const dateB = new Date(b.updated_at || b.created_at || 0);
                        return dateB - dateA; // Descending order (newest first)
                    });
                };
                if (Array.isArray(data)) {
                    setPurchaseOrders(sortByDate(data));
                    setTotalPages(1);
                    setTotalCount(data.length);
                } else if (data && (Array.isArray(data.orders) || Array.isArray(data.items) || Array.isArray(data.data))) {
                    const arr = Array.isArray(data.orders) ? data.orders : (Array.isArray(data.items) ? data.items : data.data);
                    setPurchaseOrders(sortByDate(arr));
                    setTotalPages(data.totalPages || Math.ceil((data.total || arr.length) / (params.limit || 20)));
                    setTotalCount(data.total || arr.length);
                } else {
                    // Unexpected shape, store as-is
                    setPurchaseOrders(data || []);
                    setTotalPages(0);
                    setTotalCount(Array.isArray(data) ? data.length : 0);
                }

                lastSignatureRef.current = signature;
                lastFetchTimeRef.current = Date.now();
            } else {
                // Keep the rows on screen and say what happened (policy C19).
                setError(result.error);
                showError(result.error || i18n.t('hookErrors.common.unexpected'));
            }

            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            showError(errorMessage || i18n.t('hookErrors.common.unexpected'));
            return { success: false, error: errorMessage };
        } finally {
            inFlightRef.current.delete(signature);
            setLoading(false);
        }
    }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

    const getPurchaseOrderById = useCallback(async (poId, options = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.getPurchaseOrderById(poId, options) || {};
            if (!result.success) setError(result?.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const createPurchaseOrder = useCallback(async (payload) => {
        setError(null);
        try {
            // Normalize payload to the server-expected shape before sending.
            const normalizePayload = (p = {}) => {
                const out = {};
                // Supplier: if new-supplier style fields present, nest under `supplier` or flat depending on API
                // Based on API specs, new-supplier endpoint takes flat fields for supplier info

                // Common fields
                if (p.shipping_destination) out.shipping_destination = p.shipping_destination;
                if (typeof p.warehouse_id !== 'undefined') out.warehouse_id = Number(p.warehouse_id) || 0;
                if (typeof p.customer_id !== 'undefined') out.customer_id = Number(p.customer_id) || 0;
                if (p.delivery_location_id) out.delivery_location_id = Number(p.delivery_location_id);
                if (p.contact_person) out.contact_person = p.contact_person;
                if (p.mobile_number) out.mobile_number = p.mobile_number;
                if (p.order_for) out.order_for = p.order_for;
                if (p.expected_delivery_date) out.expected_delivery_date = p.expected_delivery_date;
                if (typeof p.payment_terms !== 'undefined') out.payment_terms = p.payment_terms !== null ? String(p.payment_terms) : null;
                if (p.discount_type) out.discount_type = String(p.discount_type).toLowerCase();
                if (typeof p.discount_value !== 'undefined') out.discount_value = Number(p.discount_value);
                if (typeof p.gst_included !== 'undefined') out.gst_included = !!p.gst_included;
                if (p.pricing_option) out.pricing_option = String(p.pricing_option).toLowerCase();
                if (typeof p.sms_notification !== 'undefined') out.sms_notification = !!p.sms_notification;
                if (p.remark) out.remark = p.remark;

                // Existing Supplier
                if (p.supplier_id) out.supplier_id = Number(p.supplier_id);

                // New Supplier fields
                if (p.owner_name) out.owner_name = p.owner_name;
                if (p.company_name) out.company_name = p.company_name;
                if (p.gst_number) out.gst_number = p.gst_number;
                if (p.phone_number) out.phone_number = p.phone_number;
                if (p.email) out.email = p.email;
                if (p.address_line1) out.address_line1 = p.address_line1;
                if (p.address_line2) out.address_line2 = p.address_line2;
                if (p.city) out.city = p.city;
                if (p.state) out.state = p.state;
                if (p.country) out.country = p.country || 'India';
                if (p.zip_code) out.zip_code = p.zip_code;
                if (p.tally_name) out.tally_name = p.tally_name;
                if (typeof p.credit_period !== 'undefined') out.credit_period = Number(p.credit_period);

                // Items
                const itemsIn = Array.isArray(p.items) ? p.items : [];
                out.items = itemsIn.map(it => {
                    const mapped = {};
                    if (it.product_size_id) mapped.product_size_id = Number(it.product_size_id);
                    if (it.unit) mapped.unit = it.unit;
                    if (typeof it.length !== 'undefined') mapped.length = Number(it.length);
                    if (typeof it.quantity !== 'undefined') mapped.quantity = Number(it.quantity);
                    if (typeof it.price !== 'undefined') mapped.price = Number(it.price);
                    if (typeof it.quantity_in_pieces !== 'undefined') mapped.quantity_in_pieces = Number(it.quantity_in_pieces);
                    if (it.remark) mapped.remark = it.remark;
                    return mapped;
                }).filter(i => typeof i.product_size_id !== 'undefined');

                return out;
            };

            const normalized = normalizePayload(payload || {});

            // Prefer explicit service methods based on payload shape
            let result;
            if (payload && (payload.owner_name || payload.company_name)) {
                // payload looks like new-supplier data
                result = await purchaseOrderService.createPurchaseOrderNewSupplier(normalized) || {};
            } else {
                // existing supplier order
                result = await purchaseOrderService.createPurchaseOrderExistingSupplier(normalized) || {};
            }

            if (result.success) {
                // Prepend newly created order
                setPurchaseOrders(prev => [result.data, ...prev]);
                setTotalCount(prev => prev + 1);
                const label = result.data?.id || '';
                showCreate(i18n.t('orders.notifications.created', { id: label }));
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showCreate]);

    const updatePurchaseOrder = useCallback(async (poId, payload) => {
        setError(null);
        try {
            const result = await purchaseOrderService.updatePurchaseOrder(poId, payload) || {};
            if (result.success) {
                setPurchaseOrders(prev => prev.map(o => (String(o.id) === String(poId) ? { ...o, ...result.data } : o)));
                showUpdate(i18n.t('orders.notifications.updated', { id: String(poId) }));
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showUpdate]);

    const deletePurchaseOrder = useCallback(async (poId, options) => {
        setError(null);
        try {
            const result = await purchaseOrderService.deletePurchaseOrder(poId, options) || {};
            if (result.success) {
                setPurchaseOrders(prev => prev.filter(o => String(o.id) !== String(poId)));
                setTotalCount(prev => Math.max(0, prev - 1));
                showDelete(i18n.t('orders.notifications.deleted', { id: String(poId) }));
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showDelete]);

    const deleteAllPurchaseOrders = useCallback(async (password) => {
        setError(null);
        try {
            const result = await ordersService.bulkDeletePurchaseOrdersGlobal({ password });
            if (result.success) {
                setPurchaseOrders([]);
                setTotalCount(0);
                showDelete(formatBulkDeleteMessage(result, `All Purchase Orders deleted`));
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showDelete]);

    // `options.idKind` names what the caller selected and MUST be set deliberately:
    //   'advice' — Purchase Daybook / Invoice-Purchase rows are purchase advices
    //              (challans); the backend resolves each to its parent PO.
    //   'po'     — the Purchase Orders list; rows are purchase orders.
    // Sending PO ids under advice_ids (or the reverse) deletes whichever POs happen to
    // own advices with those numbers — the Purchase Orders page did exactly that after
    // the daybook fix relabelled this hook's payload.
    const bulkDeletePurchaseOrders = useCallback(async (ids = [], password, options = {}) => {
        const { idKind, ...requestOptions } = options || {};
        if (idKind !== 'advice' && idKind !== 'po') {
            const error = `bulkDeletePurchaseOrders: idKind must be 'advice' or 'po' (got ${String(idKind)})`;
            setError(error);
            return { success: false, error };
        }
        setError(null);
        try {
            const payload = idKind === 'po'
                ? { purchase_order_ids: ids, password }
                : { advice_ids: ids, password };
            const result = await ordersService.bulkDeleteSelectedOrders(payload, requestOptions);
            if (result.success) {
                // Rows are keyed by their own id (advice id on the daybook pages, PO id on
                // the Purchase Orders page), so remove by o.id in both modes.
                const idSet = new Set(ids.map(String));
                setPurchaseOrders(prev => prev.filter(o => !idSet.has(String(o.id))));
                setTotalCount(prev => Math.max(0, prev - ids.length));
                // Report the TRUE count from the (nested) response, not the selection size.
                showDelete(formatBulkDeleteMessage(result, i18n.t('orders.notifications.bulkDeleted', { count: String(ids.length) })));
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showDelete]);

    const confirmPurchaseOrder = useCallback(async (poId) => {
        setError(null);
        try {
            const result = await purchaseOrderService.confirmPurchaseOrder(poId) || {};
            if (result.success) {
                setPurchaseOrders(prev => prev.map(o => (String(o.id || o.po_id) === String(poId) ? { ...o, ...result.data } : o)));
                showUpdate(`Purchase Order ${poId} confirmed`);
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showUpdate]);

    const cancelPurchaseOrder = useCallback(async (poId) => {
        setError(null);
        try {
            const result = await purchaseOrderService.cancelPurchaseOrder(poId) || {};
            if (result.success) {
                setPurchaseOrders(prev => prev.map(o => (String(o.id || o.po_id) === String(poId) ? { ...o, ...result.data } : o)));
                showUpdate(`Purchase Order ${poId} cancelled`);
            } else {
                setError(result?.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [showUpdate]);

    // Purchase Advices
    const fetchPurchaseAdvices = useCallback(async (params = {}, options = {}) => {
        const { force = false } = options;
        const signature = `advice-${generateSignature({ ...params, warehouse_ids: selectedWarehouseIds })}`;

        if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
        if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

        setLoading(true);
        setError(null);
        inFlightRef.current.add(signature);

        try {
            const result = await purchaseOrderService.getAllPurchaseAdvices(params);
            if (result.success) {
                const data = result.data;
                // Sort by updated_at or created_at (newest first)
                const sortByDate = (arr) => {
                    return [...arr].sort((a, b) => {
                        const dateA = new Date(a.updated_at || a.created_at || 0);
                        const dateB = new Date(b.updated_at || b.created_at || 0);
                        return dateB - dateA; // Descending order (newest first)
                    });
                };
                if (Array.isArray(data)) {
                    setPurchaseOrders(sortByDate(data));
                    setTotalPages(1);
                    setTotalCount(data.length);
                } else if (data && (Array.isArray(data.data) || Array.isArray(data.advices) || Array.isArray(data.items))) {
                    const arr = data.data || data.advices || data.items;
                    setPurchaseOrders(sortByDate(arr));
                    setTotalPages(data.totalPages || Math.ceil((data.total || arr.length) / (params.limit || 20)));
                    setTotalCount(data.total || arr.length);
                } else {
                    setPurchaseOrders([]);
                    setTotalPages(0);
                    setTotalCount(0);
                }
                lastSignatureRef.current = signature;
                lastFetchTimeRef.current = Date.now();
            } else {
                setError(result.error);
                setPurchaseOrders([]);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            setPurchaseOrders([]);
            return { success: false, error: errorMessage };
        } finally {
            inFlightRef.current.delete(signature);
            setLoading(false);
        }
    }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

    const getPurchaseAdviceById = useCallback(async (poId) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.getPurchaseAdviceById(poId);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePurchaseAdvice = useCallback(async (poId, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.updatePurchaseAdvice(poId, payload);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const generatePurchaseAdvice = useCallback(async (poId, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.generatePurchaseAdvice(poId, payload) || {};
            if (!result.success) setError(result?.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const assignSupervisorToAdvice = useCallback(async (adviceId, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.assignSupervisorToAdvice(adviceId, payload);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);


    // Purchase Challans
    const fetchPurchaseChallans = useCallback(async (params = {}, options = {}) => {
        const { force = false } = options;
        const signature = `challan-${generateSignature({ ...params, warehouse_ids: selectedWarehouseIds })}`;

        if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
        if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

        setLoading(true);
        setError(null);
        inFlightRef.current.add(signature);

        try {
            const result = await purchaseOrderService.getAllPurchaseChallans(params);
            if (result.success) {
                const data = result.data;
                // Sort by updated_at or created_at (newest first)
                const sortByDate = (arr) => {
                    return [...arr].sort((a, b) => {
                        const dateA = new Date(a.updated_at || a.created_at || 0);
                        const dateB = new Date(b.updated_at || b.created_at || 0);
                        return dateB - dateA; // Descending order (newest first)
                    });
                };
                if (Array.isArray(data)) {
                    setPurchaseOrders(sortByDate(data));
                    setTotalPages(1);
                    setTotalCount(data.length);
                } else if (data && (Array.isArray(data.data) || Array.isArray(data.challans) || Array.isArray(data.items))) {
                    const arr = data.data || data.challans || data.items;
                    setPurchaseOrders(sortByDate(arr));
                    setTotalPages(data.totalPages || Math.ceil((data.total || arr.length) / (params.limit || 20)));
                    setTotalCount(data.total || arr.length);
                } else {
                    setPurchaseOrders([]);
                    setTotalPages(0);
                    setTotalCount(0);
                }
                lastSignatureRef.current = signature;
                lastFetchTimeRef.current = Date.now();
            } else {
                setError(result.error);
                setPurchaseOrders([]);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            setPurchaseOrders([]);
            return { success: false, error: errorMessage };
        } finally {
            inFlightRef.current.delete(signature);
            setLoading(false);
        }
    }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

    const getPurchaseChallanById = useCallback(async (poId) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.getPurchaseChallanById(poId);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const editPurchaseChallan = useCallback(async (adviceId, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.editPurchaseChallan(adviceId, payload);
            if (result.success) {
                showUpdate(`Purchase Challan updated successfully`);
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const exportPurchaseChallans = useCallback(async (params = {}) => {
        try {
            const res = await purchaseOrderService.exportPurchaseChallans(params);
            if (res.success) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                
                let filename = 'purchase_challans_export.xlsx';
                const contentDisposition = res.headers?.['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                    if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1];
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return { success: true };
            }
            return { success: false, error: res.error };
        } catch (e) {
            return { success: false, error: e.message };
        }
    }, []);

    const exportPurchaseDaybook = useCallback(async (params = {}) => {
        try {
            const res = await purchaseOrderService.exportPurchaseDaybook(params);
            if (res.success) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                
                let filename = 'purchase_daybook_export.csv';
                const contentDisposition = res.headers?.['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                    if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1];
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return { success: true };
            }
            return { success: false, error: res.error };
        } catch (e) {
            return { success: false, error: e.message };
        }
    }, []);

    const markAdviceChallanPrinted = useCallback(async (adviceId) => {
        try {
            const result = await purchaseOrderService.markAdviceChallanPrinted(adviceId);
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        }
    }, []);

    // Purchase Daybook
    const fetchPurchaseDaybook = useCallback(async (params = {}, options = {}) => {
        const { force = false } = options;
        const signature = `daybook-${generateSignature({ ...params, warehouse_ids: selectedWarehouseIds })}`;

        if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
        if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

        // Clear stale data immediately on forced refresh to prevent duplicates
        if (force) setPurchaseOrders([]);

        setLoading(true);
        setError(null);
        inFlightRef.current.add(signature);

        try {
            const result = await purchaseOrderService.getPurchaseDaybook(params);
            if (result.success) {
                const data = result.data;
                
                // Flexible sorting logic
                const sortEntries = (arr) => {
                    if (params.sort_by === 'serial_number') {
                        return [...arr].sort((a, b) => {
                            const aVal = a.serial_number || '';
                            const bVal = b.serial_number || '';
                            const comp = aVal.localeCompare(bVal, undefined, { numeric: true });
                            return params.sort_order === 'desc' ? -comp : comp;
                        });
                    }
                    
                    // Default date sorting (newest first)
                    return [...arr].sort((a, b) => {
                        const dateA = new Date(a.updated_at || a.created_at || 0);
                        const dateB = new Date(b.updated_at || b.created_at || 0);
                        return dateB - dateA;
                    });
                };

                if (Array.isArray(data)) {
                    setPurchaseOrders(sortEntries(data));
                    setTotalPages(1);
                    setTotalCount(data.length);
                } else if (data && (Array.isArray(data.items) || Array.isArray(data.data) || Array.isArray(data.daybook))) {
                    const arr = Array.isArray(data.items) ? data.items : (Array.isArray(data.data) ? data.data : data.daybook);
                    const total = data.total ?? data.count ?? arr.length;
                    setPurchaseOrders(sortEntries(arr));
                    setTotalPages(Math.ceil(total / (params.limit || 20)));
                    setTotalCount(total);
                } else {
                    setPurchaseOrders([]);
                    setTotalPages(0);
                    setTotalCount(0);
                }
                lastSignatureRef.current = signature;
                lastFetchTimeRef.current = Date.now();
            } else {
                setError(result.error);
                setPurchaseOrders([]);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            setPurchaseOrders([]);
            return { success: false, error: errorMessage };
        } finally {
            inFlightRef.current.delete(signature);
            setLoading(false);
        }
    }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

    const markAllAdvised = useCallback(async (poId) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.markAllAdvised(poId) || {};
            if (result.success) {
                setPurchaseOrders(prev => prev.map(o => (String(o.id || o.po_id) === String(poId) ? { ...o, ...result.data } : o)));
                showUpdate(`Purchase Order ${poId} marked as all advised`);
            } else {
                setError(result?.error || i18n.t('hookErrors.purchaseAdvice.markAllAdvised'));
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const clearError = useCallback(() => setError(null), []);

    return {
        // State
        purchaseOrders,
        loading,
        error,
        totalPages,
        totalCount,
        // Product sizes reference
        productSizes,
        fetchProductSizes,
        // Actions
        fetchPurchaseOrders,
        getPurchaseOrderById,
        createPurchaseOrder,
        updatePurchaseOrder,
        deletePurchaseOrder,
        bulkDeletePurchaseOrders,
        deleteAllPurchaseOrders,
        confirmPurchaseOrder,
        cancelPurchaseOrder,
        markAllAdvised,
        // Advice Actions
        fetchPurchaseAdvices,
        getPurchaseAdviceById,
        updatePurchaseAdvice,
        generatePurchaseAdvice,
        assignSupervisorToAdvice,
        // Challan Actions
        fetchPurchaseChallans,
        getPurchaseChallanById,
        editPurchaseChallan,
        exportPurchaseChallans,
        markAdviceChallanPrinted,
        // Daybook Actions
        fetchPurchaseDaybook,
        exportPurchaseDaybook,
        // Utils
        clearError,
    };
}

export default usePurchaseOrder;
